import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, Plus, Search, BookOpen, FileText } from "lucide-react";
import GuideViewer from "../components/academy/GuideViewer";
import GuideEditor from "../components/academy/GuideEditor";

const DIFF_COLORS = { base: "#059669", intermedio: "#d97706", avanzato: "#dc2626" };

export default function Academy() {
  const [meta, setMeta] = useState(null);
  const [guides, setGuides] = useState([]);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState({ mode: "list" }); // list | viewer | editor

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (category !== "all") p.set("category", category);
    if (q) p.set("q", q);
    api.get(`/academy/guides?${p.toString()}`).then((r) => setGuides(r.data)).catch(() => {});
  }, [category, q]);

  useEffect(() => { api.get("/academy/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const downloadManual = async () => {
    try {
      const r = await api.get("/academy/manual.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "manuale_urban_os.pdf"; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore generazione manuale"); }
  };

  if (view.mode === "viewer") return <GuideViewer id={view.id} meta={meta} onBack={() => { setView({ mode: "list" }); load(); }} onEdit={(g) => setView({ mode: "editor", initial: g })} />;
  if (view.mode === "editor") return <GuideEditor initial={view.initial} meta={meta} onBack={() => setView({ mode: "list" })} onSaved={(id) => { load(); setView({ mode: "viewer", id }); }} />;

  const catCount = (key) => guides.filter((g) => g.category === key).length;
  const cats = (meta?.categories || []).filter((c) => catCount(c.key) > 0 || category === c.key);

  return (
    <div className="space-y-6" data-testid="academy-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-indigo-600"><GraduationCap size={16} /> Urban Academy</div>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Il manuale d'uso di Urban OS: guide passo-passo con screenshot, callout, video, FAQ ed errori comuni. Un nuovo collaboratore impara seguendo le guide.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" onClick={downloadManual} data-testid="academy-manual-btn"><FileText size={15} /> Manuale completo PDF</Button>
          {meta?.can_draft && <Button className="gap-2" onClick={() => setView({ mode: "editor", initial: null })} data-testid="academy-new-btn"><Plus size={15} /> Nuova guida</Button>}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca guide, FAQ, procedure…" className="h-9 w-72 pl-8" data-testid="academy-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button onClick={() => setCategory("all")} className={`rounded-full px-3 py-1 text-xs font-medium ${category === "all" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid="academy-cat-all">Tutte</button>
          {(meta?.categories || []).map((c) => (
            <button key={c.key} onClick={() => setCategory(c.key)} className={`rounded-full px-3 py-1 text-xs font-medium ${category === c.key ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`} data-testid={`academy-cat-${c.key}`}>{c.label}</button>
          ))}
        </div>
      </div>

      {guides.length === 0 ? (
        <Card className="border-dashed border-slate-200 p-12 text-center" data-testid="academy-empty">
          <BookOpen className="mx-auto mb-3 text-slate-300" size={40} />
          <p className="text-sm text-slate-400">Nessuna guida ancora. {meta?.can_draft && "Crea la prima guida con \"Nuova guida\"."}</p>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {guides.map((g) => (
            <Card key={g.id} className="cursor-pointer border-slate-200 p-5 transition-shadow hover:shadow-md" onClick={() => setView({ mode: "viewer", id: g.id })} data-testid={`academy-guide-${g.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-head text-base font-semibold text-slate-800">{g.title}</h3>
                <Badge variant="outline" className="shrink-0" style={{ borderColor: DIFF_COLORS[g.difficulty], color: DIFF_COLORS[g.difficulty] }}>{g.difficulty}</Badge>
              </div>
              {g.summary && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{g.summary}</p>}
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                <span>{(meta?.categories || []).find((c) => c.key === g.category)?.label}</span>
                <span>· {(g.steps || []).length} passi</span>
                {g.status !== "pubblicata" && <Badge variant="secondary" className="text-[10px]">{g.status}</Badge>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
